import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowRight, Plus, X, Check, AlertCircle } from 'lucide-react';
import CustomCursor from '../components/CustomCursor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CY = '#90E0EF';
const UNIT_MINOR = 2000; // £20 — server is authoritative; used only for display
const MAX = 20;

function money(minor) {
  const v = (Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `£${v}`;
}
function serverError(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  return fallback;
}

const line =
  'w-full bg-transparent border-b border-[#242424] text-[#f4f4f4] py-3 focus:outline-none focus:border-[#90E0EF] placeholder:text-[#4a4a4a] transition-colors';

let ROW_SEQ = 0;
const newRow = (value = '') => ({ id: `r${++ROW_SEQ}`, value });

function StatusChip({ res }) {
  if (!res) return null;
  const map = {
    available: { c: '#7ed4a0', t: 'Available', icon: Check },
    sold: { c: '#e88ba0', t: 'Sold', icon: X },
    not_found: { c: '#e4c07a', t: 'Not found', icon: AlertCircle },
    invalid: { c: '#e4c07a', t: 'Invalid', icon: AlertCircle },
  };
  const dup = res.duplicate ? { c: '#e88ba0', t: 'Duplicate', icon: X } : null;
  const s = dup || map[res.status] || map.invalid;
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em]" style={{ color: s.c }}>
      <Icon className="w-3 h-3" /> {s.t}
    </span>
  );
}

export default function CardBuyPage() {
  const [rows, setRows] = useState([newRow()]);
  const [statuses, setStatuses] = useState({});
  const [validating, setValidating] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [terms, setTerms] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const values = useMemo(() => rows.map((r) => r.value.trim()), [rows]);
  const valuesKey = values.join('|');

  const revalidate = useCallback(async () => {
    if (values.every((v) => v === '')) { setStatuses({}); return; }
    setValidating(true);
    try {
      const { data } = await axios.post(`${API}/public/cards/validate`, { card_numbers: values });
      const map = {};
      (data.results || []).forEach((res, i) => { if (rows[i]) map[rows[i].id] = res; });
      setStatuses(map);
    } catch { /* ignore — will retry on next change */ }
    finally { setValidating(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesKey]);

  useEffect(() => {
    const t = setTimeout(revalidate, 450);
    return () => clearTimeout(t);
  }, [revalidate]);

  const filled = rows.filter((r) => r.value.trim());
  const qty = filled.length;
  const allOk = qty > 0 && filled.every((r) => statuses[r.id]?.ok);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canPay = allOk && name.trim() && emailOk && terms && !busy;

  function setValue(id, raw) {
    const v = raw.replace(/[^0-9]/g, '').slice(0, 6);
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, value: v } : r)));
    setError(null);
  }
  function addRow() {
    setRows((rs) => (rs.length >= MAX ? rs : [...rs, newRow()]));
  }
  function removeRow(id) {
    setRows((rs) => (rs.length <= 1 ? rs : rs.filter((r) => r.id !== id)));
    setStatuses((s) => { const n = { ...s }; delete n[id]; return n; });
  }

  async function pay() {
    if (!canPay) return;
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/public/cards/checkout`, {
        name: name.trim(),
        email: email.trim(),
        card_numbers: values.filter((v) => v),
        accept_terms: terms,
        marketing_opt_in: marketing,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(serverError(e, 'Could not start checkout. One of your cards may have just sold — please review.'));
      setBusy(false);
      revalidate();
    }
  }

  return (
    <div className="min-h-[100svh] bg-[#050505] text-[#f0f0f0] relative overflow-hidden" data-testid="card-buy-page">
      <CustomCursor />
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(#90E0EF 1px, transparent 1px), linear-gradient(90deg, #90E0EF 1px, transparent 1px)', backgroundSize: '44px 44px' }} />
      <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full" style={{ background: 'radial-gradient(circle, rgba(144,224,239,0.10), transparent 70%)' }} />

      <div className="relative z-10 max-w-md mx-auto px-6 py-12">
        <div className="flex items-center gap-2.5 mb-10">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: CY, boxShadow: `0 0 8px ${CY}` }} />
          <span className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CY }}>Mr. Relative</span>
        </div>

        <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#666]">Card Editions</p>
        <h1 className="font-outfit font-bold text-white tracking-tight mt-2" style={{ fontSize: '2.4rem', lineHeight: 1.02 }}>Claim your cards</h1>
        <p className="text-xs font-mono text-[#888] mt-3 leading-relaxed">
          Enter the number printed on each physical card. £20 per card. First completed payment secures the edition.
        </p>

        {/* Card rows */}
        <div className="mt-9 space-y-5" data-testid="card-rows">
          {rows.map((r, i) => {
            const res = r.value.trim() ? statuses[r.id] : null;
            return (
              <div key={r.id} data-testid={`card-row-${i}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#666]">Card {i + 1}</label>
                  {rows.length > 1 && (
                    <button onClick={() => removeRow(r.id)} data-testid={`card-remove-${i}`} style={{ cursor: 'pointer' }}
                      className="text-[#555] hover:text-[#e88ba0] transition-colors"><X className="w-3.5 h-3.5" /></button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <input
                    value={r.value}
                    inputMode="numeric"
                    placeholder="0064"
                    data-testid={`card-input-${i}`}
                    onChange={(e) => setValue(r.id, e.target.value)}
                    className={line + ' text-2xl font-mono tracking-[0.28em] flex-1'}
                  />
                  <div className="w-24 text-right shrink-0">
                    {validating && r.value.trim() && !res ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" style={{ color: CY }} /> : <StatusChip res={res} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {rows.length < MAX && (
          <button onClick={addRow} data-testid="card-add-row" style={{ cursor: 'pointer' }}
            className="mt-5 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.16em] text-[#999] hover:text-[#90E0EF] transition-colors">
            <Plus className="w-3.5 h-3.5" /> Add another card
          </button>
        )}

        {/* Running total */}
        <div className="mt-8 flex items-end justify-between border-t border-[#181818] pt-5">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#666]">{qty || 0} card{qty === 1 ? '' : 's'} × £20</p>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#4a4a4a] mt-1">Total</p>
          </div>
          <p className="font-outfit font-bold text-white" style={{ fontSize: '2.4rem', lineHeight: 1 }} data-testid="card-total">{money(qty * UNIT_MINOR)}</p>
        </div>

        {/* Buyer details */}
        <div className="mt-9 space-y-6">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#666]">Full name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="card-buy-name" className={line + ' text-base mt-1'} />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#666]">Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" type="email" data-testid="card-buy-email" className={line + ' text-base mt-1'} />
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="card-buy-terms" className="mt-0.5 accent-[#90E0EF] w-4 h-4 shrink-0" />
            <span className="text-xs font-mono text-[#c8c8c8] leading-relaxed">
              I accept the <a href="https://therelativegallery.com" target="_blank" rel="noreferrer" className="underline" style={{ color: CY }}>Terms &amp; Conditions</a> and <a href="https://therelativegallery.com" target="_blank" rel="noreferrer" className="underline" style={{ color: CY }}>Privacy Policy</a>.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} data-testid="card-buy-marketing" className="mt-0.5 accent-[#90E0EF] w-4 h-4 shrink-0" />
            <span className="text-xs font-mono text-[#999] leading-relaxed">
              Keep me posted on new drops &amp; exhibitions from The Relative Gallery. (optional)
            </span>
          </label>
        </div>

        {error && (
          <p className="text-xs font-mono text-[#e88ba0] mt-5 flex items-start gap-2" data-testid="card-buy-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
          </p>
        )}

        <button onClick={pay} disabled={!canPay} data-testid="card-buy-pay"
          style={{ cursor: canPay ? 'pointer' : 'not-allowed', background: canPay ? CY : 'transparent', color: canPay ? '#050505' : '#555', border: `1px solid ${canPay ? CY : '#242424'}` }}
          className="mt-8 w-full flex items-center justify-center gap-2 py-4 text-[11px] font-mono uppercase tracking-[0.22em] font-medium transition-all">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to payment…</> : <>Pay {money(qty * UNIT_MINOR)} <ArrowRight className="w-4 h-4" /></>}
        </button>
        <p className="text-[10px] font-mono text-[#555] mt-3 text-center">Secure payment via Stripe · No card is held until you pay</p>

        <div className="mt-12 text-center">
          <Link to="/" className="text-[11px] font-mono text-[#666] hover:text-white transition-colors">← The Relative Gallery</Link>
        </div>
      </div>
    </div>
  );
}
